import { CountryCode } from '@domains/country';

export type AccessoryRoofedValue = 0 | 1 | null;

export const ACCESSORY_AREA_KIND_OTHER = 'other';

export interface AccessoryAreaFormItem {
  id: string;
  kind: string;
  customKind?: string;
  meters: string;
  roofed: AccessoryRoofedValue;
}

/** Alineado con `isSpecificOption` + `default` de portal-pricing (AccessoryAreaMX) */
export type AccessoryRoofLockMode = 'roof' | 'not_roof' | 'both';

export interface AccessoryRoofRule {
  showRoofed: boolean;
  lockMode: AccessoryRoofLockMode;
}

export const ACCESSORY_AREA_KINDS_MX = [
  { value: 'garage', label: 'Cochera' },
  { value: 'room_service', label: 'Cuarto de servicio' },
  { value: 'private_garden', label: 'Patio privado' },
  { value: 'roof_garden', label: 'Roof garden' },
  { value: 'terrace', label: 'Terraza' },
  { value: ACCESSORY_AREA_KIND_OTHER, label: 'Otros' },
] as const;

/**
 * Reglas de techada en MX (portal-pricing AccessoryAreaMX):
 * - garage, room_service, private_garden: una opción bloqueada según lockMode
 * - roof_garden, terrace: ambas opciones habilitadas
 *
 * Valores estáticos hasta integrar defaults de GET /get_extra_area.
 */
export const ACCESSORY_ROOF_RULES_MX: Record<string, AccessoryRoofRule> = {
  garage: { showRoofed: true, lockMode: 'roof' },
  room_service: { showRoofed: true, lockMode: 'roof' },
  private_garden: { showRoofed: true, lockMode: 'not_roof' },
  roof_garden: { showRoofed: true, lockMode: 'both' },
  terrace: { showRoofed: true, lockMode: 'both' },
  other: { showRoofed: true, lockMode: 'both' },
};

export const accessoryAreaLabelByKind: Record<string, string> =
  ACCESSORY_AREA_KINDS_MX.reduce<Record<string, string>>((acc, item) => {
    acc[item.value] = item.label;
    return acc;
  }, {});

export interface AccessoryRoofFieldState {
  showRoofed: boolean;
  roofed: AccessoryRoofedValue;
  disableRoofed: boolean;
  disableNotRoofed: boolean;
}

let nextAccessoryAreaId = 0;

const createAccessoryAreaId = (): string =>
  `accessory-area-${++nextAccessoryAreaId}`;

const parseRoofedValue = (roofed: string | undefined): AccessoryRoofedValue => {
  const normalized = roofed?.toLowerCase().trim();
  if (normalized === '1' || normalized === 'true') return 1;
  if (normalized === '0' || normalized === 'false') return 0;
  return null;
};

const normalizeAccessoryMeters = (value: string | undefined): string => {
  if (!value?.trim()) return '';

  const numericValue = parseFloat(value);
  if (isNaN(numericValue)) return '';

  return Math.round(numericValue).toString();
};

const parseAccessoryAreaEntry = (
  entry: string,
  country: CountryCode = CountryCode.MX
): AccessoryAreaFormItem | null => {
  const parts = entry.split(',').map(part => part.trim());
  if (!parts.length || !parts[0]) return null;

  if (parts[0] === ACCESSORY_AREA_KIND_OTHER) {
    if (parts.length !== 3) return null;

    const [, meters, roofed] = parts;
    const parsedMeters = normalizeAccessoryMeters(meters);
    if (!parsedMeters) return null;

    return {
      id: createAccessoryAreaId(),
      kind: ACCESSORY_AREA_KIND_OTHER,
      meters: parsedMeters,
      roofed: resolveRoofedOnKindChange(
        ACCESSORY_AREA_KIND_OTHER,
        country,
        parseRoofedValue(roofed)
      ),
    };
  }

  if (parts.length < 3) return null;

  const [kind, meters, roofed] = parts;
  const parsedMeters = normalizeAccessoryMeters(meters);
  if (!kind || !parsedMeters) return null;

  return {
    id: createAccessoryAreaId(),
    kind,
    meters: parsedMeters,
    roofed: resolveRoofedOnKindChange(kind, country, parseRoofedValue(roofed)),
  };
};

const getAccessoryAreaLabel = ({
  kind,
}: Pick<AccessoryAreaFormItem, 'kind'>): string =>
  accessoryAreaLabelByKind[kind] ?? kind;

const isAccessoryAreaComplete = ({
  kind,
  meters,
}: Pick<AccessoryAreaFormItem, 'kind' | 'meters'>): boolean =>
  Boolean(kind && meters.trim());

const getRoofedDisplaySuffix = (roofed: AccessoryRoofedValue): string => {
  if (roofed === 1) return 'Techada';
  if (roofed === 0) return 'Sin techar';
  return '';
};

export const areAccessoryAreasEqual = (
  current: AccessoryAreaFormItem[] | undefined,
  next: AccessoryAreaFormItem[] | undefined,
  country: CountryCode = CountryCode.MX
): boolean =>
  serializeAccessoryAreas(current, country) === serializeAccessoryAreas(next, country);

/**
 * Parsea sugerencias del servicio (solo MX):
 * "kind,meters,roofed" — p.ej. "other,8,true" o "private_garden,31,false"
 * roofed puede venir como 0|1 o true|false
 */
export const parseAccessoryAreasSuggestion = (
  value: string | undefined,
  country: CountryCode = CountryCode.MX
): AccessoryAreaFormItem[] => {
  if (!value?.trim() || country !== CountryCode.MX) return [];

  return value
    .split(';')
    .map(entry => entry.trim())
    .filter(Boolean)
    .map(entry => parseAccessoryAreaEntry(entry, country))
    .filter((area): area is AccessoryAreaFormItem => area !== null);
};

export const formatAccessoryAreasFromItems = (
  areas: AccessoryAreaFormItem[],
  country: CountryCode = CountryCode.MX
): string => {
  if (!areas.length) return 'Sin áreas accesorias';

  return areas
    .map(area => {
      const { kind, meters, roofed } = area;
      const label = getAccessoryAreaLabel(area);
      const roofState = getAccessoryRoofFieldState(kind, country);
      let roofedLabel = '';
      if (roofState.showRoofed && roofed !== null) {
        roofedLabel = ` — ${getRoofedDisplaySuffix(roofed)}`;
      }
      return `${label}: ${meters} m²${roofedLabel}`;
    })
    .join('; ');
};

export const formatAccessoryAreaSuggestionForDisplay = (
  value: string | undefined,
  country: CountryCode = CountryCode.MX
): string =>
  formatAccessoryAreasFromItems(parseAccessoryAreasSuggestion(value, country), country);

export const getAccessoryRoofFieldState = (
  kind: string,
  country: CountryCode = CountryCode.MX
): AccessoryRoofFieldState => {
  if (country !== CountryCode.MX || !kind) {
    return {
      showRoofed: false,
      roofed: null,
      disableRoofed: true,
      disableNotRoofed: true,
    };
  }

  const rule = ACCESSORY_ROOF_RULES_MX[kind];
  if (!rule?.showRoofed) {
    return {
      showRoofed: false,
      roofed: null,
      disableRoofed: true,
      disableNotRoofed: true,
    };
  }

  if (rule.lockMode === 'roof') {
    return {
      showRoofed: true,
      roofed: 1,
      disableRoofed: false,
      disableNotRoofed: true,
    };
  }

  if (rule.lockMode === 'not_roof') {
    return {
      showRoofed: true,
      roofed: 0,
      disableRoofed: true,
      disableNotRoofed: false,
    };
  }

  return {
    showRoofed: true,
    roofed: 0,
    disableRoofed: false,
    disableNotRoofed: false,
  };
};

export const createEmptyAccessoryArea = (): AccessoryAreaFormItem => ({
  id: createAccessoryAreaId(),
  kind: '',
  meters: '',
  roofed: null,
});

export const resolveRoofedOnKindChange = (
  kind: string,
  country: CountryCode,
  currentRoofed: AccessoryRoofedValue
): AccessoryRoofedValue => {
  const state = getAccessoryRoofFieldState(kind, country);
  if (!state.showRoofed) return null;

  if (state.disableRoofed) return 0;
  if (state.disableNotRoofed) return 1;

  if (currentRoofed === 0 || currentRoofed === 1) return currentRoofed;
  return state.roofed ?? 0;
};

/**
 * Serializa áreas accesorias al formato esperado por backend (solo MX):
 * "kind,meters,roofed;kind,meters,roofed" (roofed: 1 | 0)
 */
export const serializeAccessoryAreas = (
  areas: AccessoryAreaFormItem[] | undefined,
  country: CountryCode = CountryCode.MX
): string => {
  if (country !== CountryCode.MX || !areas?.length) return '';

  return areas
    .filter(isAccessoryAreaComplete)
    .map(({ kind, meters, roofed }) => {
      const roofState = getAccessoryRoofFieldState(kind, country);
      const roofedValue = roofed ?? roofState.roofed ?? 0;
      return `${kind},${Number(meters)},${roofedValue}`;
    })
    .join(';');
};

export const parseAccessoryAreas = (
  value: string | undefined
): AccessoryAreaFormItem[] => {
  if (!value?.trim()) return [];

  return value
    .split(';')
    .map(entry => entry.trim())
    .filter(Boolean)
    .map(entry => parseAccessoryAreaEntry(entry))
    .filter((area): area is AccessoryAreaFormItem => area !== null);
};

export const formatAccessoryAreasForDisplay = (
  value: string | undefined,
  country: CountryCode = CountryCode.MX
): string =>
  formatAccessoryAreasFromItems(parseAccessoryAreas(value), country);
